/**
 * Device capability detection for background blur engine selection.
 *
 * VI-446 selection rules:
 *   - WebGL2 supported -> 'webgl2' (MediaPipe Tasks Vision + WebGL2 Shaders)
 *   - WebGL2 unsupported AND not Safari -> 'selfie-seg' (Legacy WASM)
 *   - WebGL2 unsupported AND Safari -> null (Selfie Seg WASM doesn't run on Safari)
 *   - Legacy browser, missing captureStream, or missing MediaRecorder -> null
 */

export type BlurEngine = 'webgl2' | 'selfie-seg';

export interface BrowserInfo {
  ie: boolean;
  oldChrome: boolean;
  oldFirefox: boolean;
  oldSafari: boolean;
  oldEdge: boolean;
}

export interface DeviceCapabilities {
  isLegacyBrowser: boolean;
  hasWebGL2: boolean;
  hasCaptureStream: boolean;
  hasMediaRecorder: boolean;
  isSafari: boolean;
  browserInfo: BrowserInfo;
}

export const hasWebGL2 = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    if (!gl) return false;
    // MediaPipe Tasks Vision's ImageSegmenter writes its segmentation mask to
    // a float-format render target. Without `EXT_color_buffer_float` (RGBA32F)
    // or `EXT_color_buffer_half_float` (RGBA16F) the GPU postprocessor fails
    // with `RET_CHECK can_use_f32 || can_use_f16_backup` on EVERY segmentation
    // call — observed on Mali-G52 (Galaxy A22) on Android Chrome (VI-507).
    // Without this guard the device says "yes WebGL2" but every frame errors
    // silently and the blur spinner stays loading forever. Treating those GPUs
    // as "no WebGL2" lets them fall through to the Selfie Seg WASM engine,
    // which doesn't require float render targets.
    const hasFloat32 = !!gl.getExtension('EXT_color_buffer_float');
    const hasFloat16 = !!gl.getExtension('EXT_color_buffer_half_float');
    return hasFloat32 || hasFloat16;
  } catch {
    return false;
  }
};

export const hasCaptureStream = (): boolean => {
  return typeof HTMLCanvasElement !== 'undefined' && 'captureStream' in HTMLCanvasElement.prototype;
};

export const hasMediaRecorder = (): boolean => {
  return typeof MediaRecorder !== 'undefined';
};

export const getBrowserInfo = (): BrowserInfo => {
  const ua = navigator.userAgent.toLowerCase();
  return {
    ie: /msie|trident/.test(ua),
    oldChrome: /chrome\/([0-9]+)/.test(ua) && parseInt(RegExp.$1, 10) < 70,
    oldFirefox: /firefox\/([0-9]+)/.test(ua) && parseInt(RegExp.$1, 10) < 65,
    oldSafari: /version\/([0-9]+).*safari/.test(ua) && parseInt(RegExp.$1, 10) < 12,
    oldEdge: /edge\/([0-9]+)/.test(ua) && parseInt(RegExp.$1, 10) < 18,
  };
};

export const isLegacyBrowser = (): boolean => {
  return Object.values(getBrowserInfo()).some(Boolean);
};

export const isSafari = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return /^((?!chrome|android).)*safari/i.test(ua) || /ipad|iphone|ipod/.test(ua);
};

export const getDeviceCapabilities = (): DeviceCapabilities => ({
  isLegacyBrowser: isLegacyBrowser(),
  hasWebGL2: hasWebGL2(),
  hasCaptureStream: hasCaptureStream(),
  hasMediaRecorder: hasMediaRecorder(),
  isSafari: isSafari(),
  browserInfo: getBrowserInfo(),
});

/**
 * Returns the recommended blur engine for the current device, or null if blur is unsupported.
 * Logs the choice to the console per VI-446 AC.
 */
export const getRecommendedBlurEngine = (): BlurEngine | null => {
  const caps = getDeviceCapabilities();

  if (caps.isLegacyBrowser) {
    console.log('[Blur] Engine: none (legacy browser detected)', caps.browserInfo);
    return null;
  }

  if (!caps.hasCaptureStream || !caps.hasMediaRecorder) {
    console.log('[Blur] Engine: none (missing captureStream or MediaRecorder)');
    return null;
  }

  if (caps.hasWebGL2) {
    console.log('[Blur] Engine: webgl2 (MediaPipe Tasks Vision + WebGL2 Shaders)');
    return 'webgl2';
  }

  if (caps.isSafari) {
    console.log('[Blur] Engine: none (Safari without WebGL2; Selfie Seg WASM not supported)');
    return null;
  }

  console.log('[Blur] Engine: selfie-seg (MediaPipe Selfie Segmentation Legacy WASM fallback)');
  return 'selfie-seg';
};
